import React, { useState, useEffect } from 'react';
import { X, Upload, Video, Image as ImageIcon, Sparkles } from 'lucide-react';
import { createBannerSlide } from '../../services/landing/bannerSlideService';

interface AddBannerSlideModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    nextOrderNumber?: number;
}

const COLOR_PRESETS = [
    { label: 'Magenta Co Marca', value: '#c1348b' },
    { label: 'Púrpura Imperial', value: '#61257B' },
    { label: 'Azul Corporativo', value: '#007FC8' },
    { label: 'Naranja Vibrante', value: '#ea8406' },
    { label: 'Dorado Sutil', value: '#ffd97c' },
    { label: 'Blanco Nieve', value: '#ffffff' },
];

export const AddBannerSlideModal: React.FC<AddBannerSlideModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    nextOrderNumber = 1,
}) => {
    const [tipo, setTipo] = useState<'image' | 'video'>('image');
    const [titulo, setTitulo] = useState('');
    const [tituloAcento, setTituloAcento] = useState('');
    const [colorAcento, setColorAcento] = useState('#c1348b');
    const [descripcion, setDescripcion] = useState('');
    const [textoBotonPrimario, setTextoBotonPrimario] = useState('Cotizar algún servicio');
    const [textoBotonSecundario, setTextoBotonSecundario] = useState('Hablar con un asesor');
    const [enlaceBotonSecundario, setEnlaceBotonSecundario] = useState('');
    const [videoUrl, setVideoUrl] = useState('');
    const [orden, setOrden] = useState(nextOrderNumber);
    const [activo, setActivo] = useState(true);

    const [imagenFile, setImagenFile] = useState<File | null>(null);
    const [videoFile, setVideoFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            setTipo('image');
            setTitulo('');
            setTituloAcento('');
            setColorAcento('#c1348b');
            setDescripcion('');
            setTextoBotonPrimario('Cotizar algún servicio');
            setTextoBotonSecundario('Hablar con un asesor');
            setEnlaceBotonSecundario('');
            setVideoUrl('');
            setOrden(nextOrderNumber);
            setActivo(true);
            setImagenFile(null);
            setVideoFile(null);
            setPreviewUrl(null);
            setVideoPreviewUrl(null);
            setError('');
        }
    }, [isOpen, nextOrderNumber]);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setImagenFile(file);
            const objectUrl = URL.createObjectURL(file);
            setPreviewUrl(objectUrl);
        }
    };

    const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setVideoFile(file);
            const objectUrl = URL.createObjectURL(file);
            setVideoPreviewUrl(objectUrl);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!titulo.trim() && !tituloAcento.trim()) {
            setError('Por favor ingresa al menos un título o texto de acento');
            return;
        }

        if (tipo === 'image' && !imagenFile) {
            setError('Por favor selecciona una imagen para la diapositiva');
            return;
        }

        if (tipo === 'video' && !videoFile && !videoUrl.trim()) {
            setError('Por favor sube un archivo de video o ingresa una URL de video');
            return;
        }

        setLoading(true);

        try {
            const formData = new FormData();
            formData.append('titulo', titulo.trim());
            formData.append('titulo_acento', tituloAcento.trim());
            formData.append('color_acento', colorAcento);
            formData.append('descripcion', descripcion.trim());
            formData.append('tipo', tipo);
            formData.append('texto_boton_primario', textoBotonPrimario.trim() || 'Cotizar algún servicio');
            formData.append('texto_boton_secundario', textoBotonSecundario.trim() || 'Hablar con un asesor');
            if (enlaceBotonSecundario.trim()) {
                formData.append('enlace_boton_secundario', enlaceBotonSecundario.trim());
            }
            if (videoUrl.trim()) {
                formData.append('video_url', videoUrl.trim());
            }
            formData.append('orden', String(orden));
            formData.append('activo', String(activo));

            if (tipo === 'image' && imagenFile) {
                formData.append('imagen', imagenFile);
            } else if (tipo === 'video') {
                if (videoFile) formData.append('video', videoFile);
                if (imagenFile) formData.append('imagen', imagenFile); // Poster opcional
            }

            await createBannerSlide(formData);
            onSuccess();
            onClose();
        } catch (err: any) {
            /* Browser diagnostics omitted. */
            setError(err.response?.data?.detail || 'Error al guardar la diapositiva. Revisa los datos.');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
            <div className="relative w-full max-w-2xl bg-white dark:bg-[#120a1f] rounded-2xl shadow-2xl border border-gray-200 dark:border-purple-900/40 my-8 overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-purple-900/30">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center text-[#c1348b]">
                            <Sparkles size={20} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Nueva Diapositiva del Banner</h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Personaliza imagen, video, textos y llamados a la acción</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-purple-900/30 transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {error && (
                    <div className="mx-6 mt-4 p-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/50 rounded-xl text-red-600 dark:text-red-400 text-sm">
                        {error}
                    </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-5 max-h-[75vh] overflow-y-auto custom-scroll">
                    {/* Selector de Tipo: Imagen vs Video */}
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300 mb-2">
                            Tipo de Medio de Fondo
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                type="button"
                                onClick={() => setTipo('image')}
                                className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl border font-semibold text-sm transition-all ${
                                    tipo === 'image'
                                        ? 'bg-[#c1348b]/10 border-[#c1348b] text-[#c1348b] shadow-sm'
                                        : 'border-gray-200 dark:border-purple-900/40 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-purple-900/20'
                                }`}
                            >
                                <ImageIcon size={18} />
                                <span>Fotografía / Imagen</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setTipo('video')}
                                className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl border font-semibold text-sm transition-all ${
                                    tipo === 'video'
                                        ? 'bg-[#61257B]/10 border-[#61257B] text-[#61257B] dark:text-purple-400 shadow-sm'
                                        : 'border-gray-200 dark:border-purple-900/40 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-purple-900/20'
                                }`}
                            >
                                <Video size={18} />
                                <span>Video Cinematográfico</span>
                            </button>
                        </div>
                    </div>

                    {/* Carga de Imagen */}
                    {tipo === 'image' && (
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Imagen de fondo (Recomendado: 1920x1080 o superior, nítida y luminosa)
                            </label>
                            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 dark:border-purple-900/50 border-dashed rounded-xl hover:border-[#c1348b] transition-colors bg-gray-50/50 dark:bg-purple-950/10">
                                <div className="space-y-2 text-center">
                                    {previewUrl ? (
                                        <div className="relative inline-block">
                                            <img
                                                src={previewUrl}
                                                alt="Preview"
                                                className="max-h-48 rounded-lg shadow-md object-cover"
                                            />
                                            <span className="block mt-2 text-xs text-green-600 font-semibold">
                                                Imagen seleccionada: {imagenFile?.name}
                                            </span>
                                        </div>
                                    ) : (
                                        <Upload className="mx-auto h-12 w-12 text-gray-400" />
                                    )}
                                    <div className="flex text-sm text-gray-600 dark:text-gray-400 justify-center">
                                        <label className="relative cursor-pointer rounded-md font-semibold text-[#c1348b] hover:text-[#9B004A] focus-within:outline-none">
                                            <span>Subir archivo de imagen</span>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                className="sr-only"
                                                onChange={handleImageChange}
                                            />
                                        </label>
                                    </div>
                                    <p className="text-xs text-gray-500">PNG, JPG, WEBP hasta 10MB</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Carga de Video */}
                    {tipo === 'video' && (
                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    Archivo de Video (MP4 / WebM)
                                </label>
                                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 dark:border-purple-900/50 border-dashed rounded-xl hover:border-[#61257B] transition-colors bg-gray-50/50 dark:bg-purple-950/10">
                                    <div className="space-y-2 text-center">
                                        {videoPreviewUrl ? (
                                            <div className="relative inline-block">
                                                <video
                                                    src={videoPreviewUrl}
                                                    controls
                                                    muted
                                                    className="max-h-44 rounded-lg shadow-md"
                                                />
                                                <span className="block mt-2 text-xs text-purple-600 font-semibold">
                                                    Video seleccionado: {videoFile?.name}
                                                </span>
                                            </div>
                                        ) : (
                                            <Video className="mx-auto h-12 w-12 text-gray-400" />
                                        )}
                                        <div className="flex text-sm text-gray-600 dark:text-gray-400 justify-center">
                                            <label className="relative cursor-pointer rounded-md font-semibold text-[#61257B] dark:text-purple-400 hover:underline">
                                                <span>Subir video local</span>
                                                <input
                                                    type="file"
                                                    accept="video/mp4,video/webm"
                                                    className="sr-only"
                                                    onChange={handleVideoChange}
                                                />
                                            </label>
                                        </div>
                                        <p className="text-xs text-gray-500">MP4 o WebM optimizado (recomendado &lt; 25MB)</p>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    O ingresa una URL de Video (ej. /videos/sample.mp4 o enlace CDN)
                                </label>
                                <input
                                    type="text"
                                    value={videoUrl}
                                    onChange={(e) => setVideoUrl(e.target.value)}
                                    placeholder="/videos/sample.mp4"
                                    className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-purple-900/40 bg-white dark:bg-[#190f2b] text-gray-900 dark:text-white text-sm focus:outline-none focus:border-[#c1348b]"
                                />
                            </div>
                        </div>
                    )}

                    {/* Títulos */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Título Principal
                            </label>
                            <input
                                type="text"
                                value={titulo}
                                onChange={(e) => setTitulo(e.target.value)}
                                placeholder="Ej: COMARCA o DECISIONES QUE"
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-purple-900/40 bg-white dark:bg-[#190f2b] text-gray-900 dark:text-white text-sm focus:outline-none focus:border-[#c1348b]"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Texto con Acento (Color Destacado)
                            </label>
                            <input
                                type="text"
                                value={tituloAcento}
                                onChange={(e) => setTituloAcento(e.target.value)}
                                placeholder="Ej: GROUP o TRANSFORMAN NEGOCIOS"
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-purple-900/40 bg-white dark:bg-[#190f2b] text-gray-900 dark:text-white text-sm focus:outline-none focus:border-[#c1348b]"
                            />
                        </div>
                    </div>

                    {/* Selector de Color de Acento */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                            Color del Texto Destacado
                        </label>
                        <div className="flex items-center gap-2 flex-wrap">
                            {COLOR_PRESETS.map((p) => (
                                <button
                                    key={p.value}
                                    type="button"
                                    onClick={() => setColorAcento(p.value)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                                        colorAcento === p.value
                                            ? 'border-gray-900 dark:border-white shadow-sm scale-105'
                                            : 'border-gray-200 dark:border-purple-900/30'
                                    }`}
                                >
                                    <span
                                        className="w-3.5 h-3.5 rounded-full border border-black/10 inline-block"
                                        style={{ backgroundColor: p.value }}
                                    />
                                    <span className="text-gray-700 dark:text-gray-300">{p.label}</span>
                                </button>
                            ))}
                            <div className="flex items-center gap-1.5 ml-auto">
                                <input
                                    type="color"
                                    value={colorAcento}
                                    onChange={(e) => setColorAcento(e.target.value)}
                                    className="w-8 h-8 rounded-lg cursor-pointer border border-gray-300 p-0.5"
                                />
                                <span className="text-xs text-gray-500 font-mono">{colorAcento}</span>
                            </div>
                        </div>
                    </div>

                    {/* Descripción */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                            Descripción / Subtítulo
                        </label>
                        <textarea
                            rows={3}
                            value={descripcion}
                            onChange={(e) => setDescripcion(e.target.value)}
                            placeholder="Texto descriptivo que acompaña a la diapositiva..."
                            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-purple-900/40 bg-white dark:bg-[#190f2b] text-gray-900 dark:text-white text-sm focus:outline-none focus:border-[#c1348b]"
                        />
                    </div>

                    {/* Botones CTA */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Texto Botón 1 (Abre Cotizador)
                            </label>
                            <input
                                type="text"
                                value={textoBotonPrimario}
                                onChange={(e) => setTextoBotonPrimario(e.target.value)}
                                placeholder="Cotizar algún servicio"
                                className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-purple-900/40 bg-white dark:bg-[#190f2b] text-gray-900 dark:text-white text-sm focus:outline-none focus:border-[#c1348b]"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Texto Botón 2 (Asesor WhatsApp)
                            </label>
                            <input
                                type="text"
                                value={textoBotonSecundario}
                                onChange={(e) => setTextoBotonSecundario(e.target.value)}
                                placeholder="Hablar con un asesor"
                                className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-purple-900/40 bg-white dark:bg-[#190f2b] text-gray-900 dark:text-white text-sm focus:outline-none focus:border-[#c1348b]"
                            />
                        </div>
                    </div>

                    {/* Enlace WhatsApp personalizado opcional */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                            Enlace personalizado para Botón 2 (Opcional - WhatsApp / Teléfono)
                        </label>
                        <input
                            type="text"
                            value={enlaceBotonSecundario}
                            onChange={(e) => setEnlaceBotonSecundario(e.target.value)}
                            placeholder="Dejar en blanco para usar el WhatsApp general predeterminado"
                            className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-purple-900/40 bg-white dark:bg-[#190f2b] text-gray-900 dark:text-white text-sm focus:outline-none focus:border-[#c1348b]"
                        />
                    </div>

                    {/* Orden y Activo */}
                    <div className="grid grid-cols-2 gap-4 items-center pt-2">
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Posición / Orden numérico
                            </label>
                            <input
                                type="number"
                                min={0}
                                value={orden}
                                onChange={(e) => setOrden(parseInt(e.target.value) || 0)}
                                className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-purple-900/40 bg-white dark:bg-[#190f2b] text-gray-900 dark:text-white text-sm focus:outline-none focus:border-[#c1348b]"
                            />
                        </div>
                        <div className="flex items-center gap-3 pt-5">
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={activo}
                                    onChange={(e) => setActivo(e.target.checked)}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#c1348b]"></div>
                                <span className="ml-3 text-sm font-semibold text-gray-800 dark:text-gray-200">
                                    {activo ? 'Visible en Landing' : 'Oculto / Inactivo'}
                                </span>
                            </label>
                        </div>
                    </div>

                    {/* Footer Buttons */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-purple-900/30">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            className="px-5 py-2.5 rounded-xl border border-gray-300 dark:border-purple-900/40 text-gray-700 dark:text-gray-300 font-semibold text-sm hover:bg-gray-50 dark:hover:bg-purple-900/20 transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-[#c1348b] to-[#61257B] text-white font-semibold text-sm shadow-lg shadow-purple-900/25 hover:opacity-95 transition-all disabled:opacity-50"
                        >
                            {loading ? 'Guardando...' : 'Crear Diapositiva'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddBannerSlideModal;
